import React, { useRef, useState, useEffect } from 'react';
import { api, formatApiError } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Upload, Check, Signature, FileText, DownloadSimple, FilePdf, MagicWand, PencilSimple, Trash, CalendarBlank } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ScheduleReminderDialog } from './ScheduleReminderDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

export const ClientOnboardingTab = ({
  client, templates, generatedLetters,
  onRefreshClient, onRefreshLetters,
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [signing, setSigning] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleLetter, setScheduleLetter] = useState(null);
  const [editLetterOpen, setEditLetterOpen] = useState(false);
  const [editLetterData, setEditLetterData] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    if (canvasRef.current && !client.signature) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  }, [client.signature]);

  const handleGenerateFromTemplate = async () => {
    if (!selectedTemplate) { toast.error('Please select a template'); return; }
    setGenerating(true);
    try {
      const { data } = await api.post('/letter-templates/generate-pdf', { template_id: selectedTemplate, client_id: client.id });
      await api.post(`/clients/${client.id}/attach-letter`, { letter_id: data.letter_id, letter_type: 'letter_of_engagement' });
      toast.success('Letter generated and attached to client!');
      onRefreshClient(); onRefreshLetters(); setSelectedTemplate('');
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setGenerating(false); }
  };

  const handleDownloadLetter = async (letter) => {
    try {
      const response = await api.get(`/generated-letters/${letter.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = letter.filename;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download letter'); }
  };

  const handleDeleteLetter = async (letterId) => {
    if (!window.confirm('Delete this generated letter?')) return;
    try {
      await api.delete(`/generated-letters/${letterId}`);
      toast.success('Letter deleted');
      onRefreshLetters();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handleEditLetter = async (letter) => {
    try {
      const { data } = await api.post(`/generated-letters/${letter.id}/get-content`);
      setEditLetterData({ ...data, letter });
      setEditContent(data.content);
      setEditTitle(data.template_name);
      setEditLetterOpen(true);
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handleSaveEditedLetter = async () => {
    if (!editLetterData || !editContent.trim()) return;
    setRegenerating(true);
    try {
      await api.post(`/generated-letters/${editLetterData.letter_id}/regenerate`, {
        content: editContent,
        title: editTitle,
      });
      toast.success('Letter updated and PDF re-generated!');
      setEditLetterOpen(false);
      onRefreshClient();
      onRefreshLetters();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setRegenerating(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const formData = new FormData(); formData.append('file', file);
    try {
      await api.post(`/clients/${client.id}/upload-loe`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Letter of Engagement uploaded successfully'); onRefreshClient();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setUploading(false); }
  };

  // Signature helpers
  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    return { x: (e.clientX || e.touches[0].clientX) - rect.left, y: (e.clientY || e.touches[0].clientY) - rect.top };
  };
  const startDrawing = (e) => { const c = canvasRef.current; const ctx = c.getContext('2d'); const p = getPos(e, c); ctx.beginPath(); ctx.moveTo(p.x, p.y); setIsDrawing(true); };
  const draw = (e) => { if (!isDrawing) return; const c = canvasRef.current; const ctx = c.getContext('2d'); const p = getPos(e, c); ctx.lineTo(p.x, p.y); ctx.strokeStyle='#1a1a1a'; ctx.lineWidth=2; ctx.lineCap='round'; ctx.stroke(); };
  const stopDrawing = () => setIsDrawing(false);
  const clearSignature = () => { const c = canvasRef.current; const ctx = c.getContext('2d'); ctx.fillStyle='white'; ctx.fillRect(0,0,c.width,c.height); };

  const saveSignature = async () => {
    setSigning(true);
    try {
      await api.post(`/clients/${client.id}/signature`, { signature: canvasRef.current.toDataURL('image/png') });
      toast.success('Signature saved! Client has been onboarded.'); onRefreshClient();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSigning(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="heading-4 flex items-center gap-2"><FileText className="w-5 h-5" /> Client Documents</CardTitle>
          <CardDescription>Generate documents from templates or upload manually for the client to sign</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {generatedLetters.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">Documents on Portal</h4>
              {generatedLetters.map((letter) => (
                <div key={letter.id} className="flex items-center justify-between p-3 bg-green-50 rounded-md border border-green-200">
                  <div className="flex items-center gap-3">
                    <FilePdf className="w-4 h-4 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-800">{letter.template_name}</p>
                      <p className="text-xs text-green-600">Generated {new Date(letter.generated_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleDownloadLetter(letter)} data-testid={`download-letter-${letter.id}`}>
                      <DownloadSimple className="w-4 h-4 mr-1" /> Download
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEditLetter(letter)} data-testid={`edit-letter-${letter.id}`}>
                      <PencilSimple className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setScheduleLetter(letter); setScheduleOpen(true); }} data-testid={`schedule-letter-${letter.id}`}>
                      <CalendarBlank className="w-3.5 h-3.5 mr-1" /> Schedule
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteLetter(letter.id)} data-testid={`delete-letter-${letter.id}`}>
                      <Trash className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {generatedLetters.length === 0 && client.letter_of_engagement && (
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-md border border-green-200">
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-600" />
                <span className="text-green-800 font-medium">{client.loe_filename || 'Document attached'}</span>
              </div>
              <Button variant="outline" size="sm" onClick={async () => {
                try {
                  const response = await api.get(`/files/${client.letter_of_engagement}`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const a = document.createElement('a'); a.href = url;
                  a.download = client.loe_filename || `Document_${client.name.replace(/\s+/g, '_')}.pdf`;
                  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                } catch { toast.error('Failed to download file'); }
              }} data-testid="download-loe-btn">
                <DownloadSimple className="w-4 h-4 mr-1" /> Download
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 border-2 border-primary/20 rounded-md bg-primary/5">
              <div className="flex items-center gap-2 mb-4">
                <MagicWand className="w-5 h-5 text-primary" />
                <h4 className="font-medium">Generate from Template</h4>
                <Badge variant="secondary" className="text-xs">Recommended</Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {client.letter_of_engagement ? 'Generate another letter or replace existing' : 'Auto-fill client details from your saved templates'}
              </p>
              <div className="space-y-3">
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger data-testid="loe-template-select"><SelectValue placeholder="Select a template" /></SelectTrigger>
                  <SelectContent>
                    {templates.length > 0 ? templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    )) : <SelectItem value="none" disabled>No templates available</SelectItem>}
                  </SelectContent>
                </Select>
                <Button onClick={handleGenerateFromTemplate} disabled={!selectedTemplate || generating} className="w-full" data-testid="generate-loe-btn">
                  {generating ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Generating...</> : <><FilePdf className="w-4 h-4 mr-2" />Generate & Attach</>}
                </Button>
                {templates.length === 0 && <p className="text-xs text-muted-foreground"><a href="/letter-templates" className="text-primary hover:underline">Create a template</a> first</p>}
              </div>
            </div>
            <div className="p-6 border-2 border-dashed border-border rounded-md">
              <div className="flex items-center gap-2 mb-4">
                <Upload className="w-5 h-5 text-muted-foreground" /><h4 className="font-medium">Manual Upload</h4>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {client.letter_of_engagement ? 'Upload another document or replace existing' : 'Upload an existing document'}
              </p>
              <input type="file" id="loe-upload" accept=".pdf,.doc,.docx" onChange={handleFileUpload} className="hidden" data-testid="loe-upload-input" />
              <Button variant="outline" onClick={() => document.getElementById('loe-upload').click()} disabled={uploading} className="w-full" data-testid="loe-upload-btn">
                {uploading ? 'Uploading...' : 'Select File'}
              </Button>
              <p className="text-xs text-muted-foreground mt-2 text-center">PDF, DOC, DOCX</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="heading-4 flex items-center gap-2"><Signature className="w-5 h-5" /> Client Signature</CardTitle>
          <CardDescription>Have the client sign below to complete the onboarding process</CardDescription>
        </CardHeader>
        <CardContent>
          {client.signature ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-md border border-green-200">
                <Check className="w-5 h-5 text-green-600" /><span className="text-green-800 font-medium">Signature captured</span>
              </div>
              <div className="p-4 bg-white rounded-md border border-border">
                <img src={client.signature} alt="Client signature" className="max-h-32" data-testid="signature-image" />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="border border-border rounded-md overflow-hidden">
                <canvas ref={canvasRef} width={500} height={200} className="signature-canvas w-full"
                  onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} data-testid="signature-canvas" />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={clearSignature} data-testid="clear-signature-btn">Clear</Button>
                <Button onClick={saveSignature} disabled={signing || !client.letter_of_engagement} data-testid="save-signature-btn">
                  {signing ? 'Saving...' : 'Save Signature & Complete Onboarding'}
                </Button>
              </div>
              {!client.letter_of_engagement && (
                <p className="text-sm text-muted-foreground">Please upload the Letter of Engagement first before collecting the signature.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Letter Dialog */}
      <Dialog open={editLetterOpen} onOpenChange={setEditLetterOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Letter</DialogTitle>
            <DialogDescription>Edit the content below and save to re-generate the PDF for {editLetterData?.client_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label className="text-sm">Letter Title</Label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} data-testid="edit-letter-title" />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Letter Content</Label>
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                rows={18}
                className="font-serif text-sm leading-relaxed"
                data-testid="edit-letter-content"
              />
            </div>
            <div className="flex justify-end gap-3 pt-3 border-t">
              <Button variant="outline" onClick={() => setEditLetterOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveEditedLetter} disabled={regenerating} data-testid="save-edited-letter-btn">
                {regenerating ? 'Saving & Re-generating PDF...' : 'Save & Re-generate PDF'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Reminder Dialog for letters */}
      {scheduleLetter && (
        <ScheduleReminderDialog
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          clientId={client.id}
          type="document"
          documentPath={scheduleLetter.file_path}
          documentName={scheduleLetter.template_name}
          templateId={scheduleLetter.template_id}
          templateName={scheduleLetter.template_name}
          onCreated={onRefreshLetters}
        />
      )}
    </div>
  );
};
